import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Users, Bell, PlusCircle, CalendarCheck, Link2, Copy, Check, RefreshCw, BarChart3, ChevronDown, Megaphone, CalendarClock, Send } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { PushStatsCard } from "@/components/PushStatsCard";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell,
} from "recharts";

export default function Customer() {
  const [phone, setPhone] = useState("");
  const [months, setMonths] = useState(3);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  // v84.106 — Counter dari backend (mencakup manual + auto system)
  const [pendingCount, setPendingCount] = useState({ count: 0, manual: 0, auto: 0 });
  // v85.18 — Reminder Analytics widget state
  const [raRange, setRaRange] = useState(6);            // 3 | 6 | 12
  const [raData, setRaData] = useState(null);
  const [raLoading, setRaLoading] = useState(false);
  // v85.18 — Riwayat Manual Reminder expand (preview 5 → +10 per klik)
  const [visibleCount, setVisibleCount] = useState(5);
  // v85.24 — Toggle master pesan otomatis
  const [autoMsg, setAutoMsg] = useState({
    followup_enabled: true, reminder_enabled: true, force_qo_enabled: true,
    updated_at: null, updated_by: null,
  });
  const [autoMsgLoading, setAutoMsgLoading] = useState(false);
  // v84.108 — Quick Order token state
  const [qoPhone, setQoPhone] = useState("");
  const [qoName, setQoName] = useState("");
  const [qoUrl, setQoUrl] = useState("");
  const [qoBusy, setQoBusy] = useState(false);
  const [qoCopied, setQoCopied] = useState(false);

  async function qoLookup() {
    if (!qoPhone) { toast.warning("Isi nomor dulu"); return; }
    setQoBusy(true);
    try {
      const r = await api.orderTokenLookup(qoPhone);
      if (r.exists) { setQoUrl(r.url); toast.success("Link ditemukan"); }
      else { setQoUrl(""); toast.info("Belum ada. Klik Generate."); }
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setQoBusy(false); }
  }
  async function qoGenerate() {
    if (!qoPhone) { toast.warning("Isi nomor dulu"); return; }
    setQoBusy(true);
    try {
      const r = await api.orderTokenGen(qoPhone, qoName);
      setQoUrl(r.url);
      toast.success("Link personal dibuat");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setQoBusy(false); }
  }
  async function qoCopy() {
    if (!qoUrl) return;
    try { await navigator.clipboard.writeText(qoUrl); }
    catch {
      const ta = document.createElement("textarea"); ta.value = qoUrl;
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } finally { document.body.removeChild(ta); }
    }
    setQoCopied(true); setTimeout(() => setQoCopied(false), 2000);
  }

  async function load() {
    try {
      const [list, count] = await Promise.all([
        api.reminderManualList(),
        api.reminderPendingCount(),
      ]);
      setItems(list.items || []);
      setPendingCount(count || { count: 0, manual: 0, auto: 0 });
    } catch (e) { /* ignore */ }
  }
  useEffect(() => { load(); }, []);

  // v85.18 — Load analytics saat mount & setiap kali range berubah
  async function loadAnalytics(months) {
    setRaLoading(true);
    try {
      const d = await api.reminderAnalytics(months);
      setRaData(d);
    } catch (e) {
      toast.error("Gagal memuat analytics: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRaLoading(false);
    }
  }
  useEffect(() => { loadAnalytics(raRange); }, [raRange]);

  // v85.24 — Load auto-msg settings (sekali di mount)
  useEffect(() => {
    api.autoMsgSettingsGet()
      .then(d => setAutoMsg(d))
      .catch(() => {});
  }, []);
  async function updateAutoMsg(key, value) {
    const prev = autoMsg[key];
    setAutoMsg(s => ({ ...s, [key]: value }));   // optimistic
    setAutoMsgLoading(true);
    try {
      const d = await api.autoMsgSettingsPut({ [key]: value });
      setAutoMsg(d);
      const label = {
        followup_enabled: "Follow-up & Tracking",
        reminder_enabled: "Reminder",
        force_qo_enabled: "CTA Quick Order",
      }[key] || key;
      toast.success(`${label} ${value ? "DIAKTIFKAN" : "DIMATIKAN"}`);
    } catch (e) {
      setAutoMsg(s => ({ ...s, [key]: prev }));  // rollback
      toast.error("Gagal update: " + (e?.response?.data?.detail || e.message));
    } finally {
      setAutoMsgLoading(false);
    }
  }

  // v85.18 — Reset visibleCount tiap kali list di-reload
  useEffect(() => { setVisibleCount(5); }, [items.length]);

  const donutData = useMemo(() => {
    const b = raData?.response_breakdown || {};
    const data = [
      { name: "Reschedule", value: b.reschedule || 0, color: "#10b981" },
      { name: "Decline", value: b.declined || 0, color: "#ef4444" },
      { name: "Respon Lain", value: b.other_response || 0, color: "#f59e0b" },
      { name: "Belum Membalas", value: b.no_response || 0, color: "#71717a" },
    ].filter(d => d.value > 0);
    return data;
  }, [raData]);

  async function save() {
    if (!phone.trim()) { toast.error("Nomor telepon wajib diisi"); return; }
    if (!(months >= 1 && months <= 24)) { toast.error("Bulan 1-24"); return; }
    setSaving(true);
    try {
      const r = await api.reminderManualCreate({ phone: phone.trim(), months, name, note });
      toast.success(`Reminder dijadwalkan untuk ${r.target_date}`);
      setPhone(""); setNote(""); setName(""); setMonths(3);
      await load();
    } catch (e) {
      toast.error("Gagal: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  }

  return (
    <div className="space-y-4" data-testid="customer-page">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-emerald-700" />
        <h1 className="text-lg font-bold tracking-tight">Customer</h1>
      </div>

      {/* v84.108 — Quick Order Personal Link admin panel */}
      <div className="border border-border rounded-sm bg-white p-4" data-testid="quick-order-panel">
        <div className="flex items-center gap-2 mb-3">
          <Link2 className="w-4 h-4 text-orange-700" />
          <h2 className="font-semibold">Personal Order Link</h2>
          <span className="text-[11px] text-zinc-500">
            Generate link pribadi per customer untuk repeat order tanpa chat
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
          <div className="md:col-span-2">
            <label className="text-[10px] text-zinc-600 block mb-0.5">Nomor Customer *</label>
            <Input value={qoPhone} onChange={e => setQoPhone(e.target.value)}
              placeholder="0812xxxxxxxx" className="h-8 rounded-sm text-[12px]"
              data-testid="qo-phone" />
          </div>
          <div>
            <label className="text-[10px] text-zinc-600 block mb-0.5">Nama (opsional)</label>
            <Input value={qoName} onChange={e => setQoName(e.target.value)}
              placeholder="Kak Budi" className="h-8 rounded-sm text-[12px]"
              data-testid="qo-name" />
          </div>
          <div className="flex items-end gap-1">
            <Button onClick={qoLookup} variant="outline" size="sm" disabled={qoBusy}
              className="rounded-sm h-8 flex-1" data-testid="qo-lookup">
              Lookup
            </Button>
            <Button onClick={qoGenerate} size="sm" disabled={qoBusy}
              className="rounded-sm h-8 flex-1 bg-orange-600 hover:bg-orange-700"
              data-testid="qo-generate">
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Generate
            </Button>
          </div>
        </div>
        {qoUrl && (
          <div className="mt-2 flex items-center gap-2 bg-orange-50 border border-orange-300 rounded-sm px-3 py-2">
            <Link2 className="w-4 h-4 text-orange-700 flex-shrink-0" />
            <a href={qoUrl} target="_blank" rel="noopener noreferrer"
              className="text-[12px] text-orange-800 font-mono truncate flex-1 hover:underline"
              data-testid="qo-url">{qoUrl}</a>
            <button onClick={qoCopy}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold border transition ${
                qoCopied ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                         : "bg-white text-orange-700 border-orange-300 hover:bg-orange-100"
              }`}
              data-testid="qo-copy">
              {qoCopied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
            </button>
          </div>
        )}
      </div>

      <div className="border border-border rounded-sm bg-white p-4">
        <div className="flex items-center gap-2 mb-3">
          <Bell className="w-4 h-4 text-amber-700" />
          <h2 className="font-semibold">Manual Reminder Service</h2>
          <span className="text-[11px] text-zinc-500">
            Admin input nomor + bulan → sistem otomatis hitung & jadwalkan
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
          <div>
            <label className="text-[10px] text-zinc-600 block mb-0.5">Nomor Telepon *</label>
            <Input value={phone} onChange={e => setPhone(e.target.value)}
              placeholder="0812xxxxxxxx" className="h-8 rounded-sm text-[12px]"
              data-testid="mr-phone" />
          </div>
          <div>
            <label className="text-[10px] text-zinc-600 block mb-0.5">Jumlah Bulan *</label>
            <select value={months} onChange={e => setMonths(parseInt(e.target.value))}
              className="h-8 rounded-sm text-[12px] border border-input px-2 w-full"
              data-testid="mr-months">
              {Array.from({length: 12}, (_, i) => i + 1).map(m => <option key={m} value={m}>{m} bulan</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-zinc-600 block mb-0.5">Nama (opsional)</label>
            <Input value={name} onChange={e => setName(e.target.value)}
              className="h-8 rounded-sm text-[12px]" data-testid="mr-name" />
          </div>
          <div>
            <label className="text-[10px] text-zinc-600 block mb-0.5">Catatan (opsional)</label>
            <Input value={note} onChange={e => setNote(e.target.value)}
              className="h-8 rounded-sm text-[12px]" data-testid="mr-note" />
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-sm gap-1.5 bg-emerald-700 hover:bg-emerald-800" data-testid="mr-save">
          <PlusCircle className="w-3.5 h-3.5" />
          {saving ? "Menyimpan…" : "Simpan & Jadwalkan"}
        </Button>
      </div>

      {/* v84.106 — Counter customer terjadwal (belum dikirim reminder) — MANUAL + AUTO */}
      <div className="border-2 border-emerald-500 bg-emerald-50 rounded-sm px-4 py-3 flex items-center justify-between"
           data-testid="scheduled-customers-counter">
        <div className="flex items-center gap-3">
          <div className="bg-emerald-600 rounded-full p-2">
            <CalendarCheck className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-bold">
              Kostumer Terjadwalkan
            </div>
            <div className="text-[11px] text-emerald-700/80 mt-0.5">
              Belum dikirim reminder WA · Manual: {pendingCount.manual} · Sistem: {pendingCount.auto}
            </div>
          </div>
        </div>
        <div className="text-3xl font-black text-emerald-700 tabular-nums" data-testid="scheduled-count">
          = {pendingCount.count}
        </div>
      </div>

      <div className="border border-border rounded-sm bg-white overflow-x-auto" data-testid="manual-reminder-history">
        <div className="px-4 py-2 border-b border-border bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center justify-between">
          <span>Riwayat Manual Reminder</span>
          <span className="text-[10px] text-zinc-400 normal-case tracking-normal font-normal">
            {items.length > 0 && `Menampilkan ${Math.min(visibleCount, items.length)} dari ${items.length}`}
          </span>
        </div>
        <table className="w-full text-[12px]">
          <thead className="border-b border-border bg-zinc-50/40">
            <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
              <th className="px-3 py-2">Tgl Input</th>
              <th className="px-3 py-2">Phone</th>
              <th className="px-3 py-2">Nama</th>
              <th className="px-3 py-2 text-center">Bulan</th>
              <th className="px-3 py-2 text-center">Target</th>
              <th className="px-3 py-2 text-center">Status</th>
              <th className="px-3 py-2">Admin</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center text-zinc-400 py-4">Belum ada entri</td></tr>
            )}
            {items.slice(0, visibleCount).map((r, i) => (
              <tr key={i} className="border-b border-border last:border-0 hover:bg-zinc-50/60">
                <td className="px-3 py-2 font-mono text-[11px]">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2 font-mono">{r.phone_norm}</td>
                <td className="px-3 py-2">{r.name || "—"}</td>
                <td className="px-3 py-2 text-center font-semibold">{r.months}</td>
                <td className="px-3 py-2 text-center font-mono">{r.target_date}</td>
                <td className="px-3 py-2 text-center">
                  <Badge className={`text-[9px] uppercase rounded-sm px-1.5 py-0 ${
                    r.schedule_status === "scheduled" ? "bg-emerald-100 text-emerald-800" :
                    r.schedule_status === "invited" ? "bg-blue-100 text-blue-800" :
                    r.schedule_status === "declined" ? "bg-red-100 text-red-800" :
                    "bg-zinc-100 text-zinc-700"
                  }`}>{r.schedule_status || "-"}</Badge>
                </td>
                <td className="px-3 py-2 text-[10px] text-zinc-500">{r.created_by || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length > visibleCount && (
          <div className="px-4 py-2 border-t border-border bg-zinc-50/50 flex justify-center">
            <Button
              onClick={() => setVisibleCount(c => Math.min(c + 10, items.length))}
              variant="outline" size="sm"
              className="rounded-sm h-7 text-[11px] gap-1 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              data-testid="mr-load-more">
              <ChevronDown className="w-3 h-3" />
              Lihat {Math.min(10, items.length - visibleCount)} Lainnya
            </Button>
          </div>
        )}
      </div>

      {/* v85.18 — Reminder Analytics widget */}
      <div className="border border-border rounded-sm bg-white p-4" data-testid="reminder-analytics-widget">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-indigo-700" />
            <h2 className="font-semibold">Reminder Analytics</h2>
            <span className="text-[11px] text-zinc-500">
              {raLoading ? "Memuat…" :
                raData ? `${raData.totals.sent} pesan · ${raData.totals.unique_recipients} customer · ${raData.totals.responded} membalas`
                       : ""}
            </span>
          </div>
          <div className="inline-flex rounded-sm border border-border overflow-hidden" data-testid="ra-range-toggle">
            {[3, 6, 12].map(n => (
              <button
                key={n}
                onClick={() => setRaRange(n)}
                className={`px-3 py-1 text-[11px] font-semibold transition ${
                  raRange === n
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-zinc-600 hover:bg-zinc-50"
                }`}
                data-testid={`ra-range-${n}`}>
                {n} bulan
              </button>
            ))}
          </div>
        </div>

        {raData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Bar: Template vs Free Text per bulan */}
            <div className="border border-border rounded-sm p-3" data-testid="ra-bar-chart">
              <div className="text-[11px] font-semibold text-zinc-700 mb-2">
                Template vs Free Text / Bulan
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={raData.monthly} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="template" stackId="a" fill="#4f46e5" name="Template" />
                  <Bar dataKey="free_text" stackId="a" fill="#f59e0b" name="Free Text" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Line: Response Rate trend */}
            <div className="border border-border rounded-sm p-3" data-testid="ra-line-chart">
              <div className="text-[11px] font-semibold text-zinc-700 mb-2">
                Response Rate Trend (%)
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={raData.monthly} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} unit="%" />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 4 }}
                    formatter={(v) => `${v}%`} />
                  <Line type="monotone" dataKey="response_rate" stroke="#10b981"
                    strokeWidth={2} dot={{ r: 3 }} name="Response Rate" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Donut: Response breakdown */}
            <div className="border border-border rounded-sm p-3 lg:col-span-2" data-testid="ra-donut-chart">
              <div className="text-[11px] font-semibold text-zinc-700 mb-2">
                Breakdown Respon Customer ({raRange} bulan)
              </div>
              {donutData.length === 0 ? (
                <div className="h-[200px] flex items-center justify-center text-[11px] text-zinc-400">
                  Belum ada data respon
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={donutData}
                      dataKey="value" nameKey="name"
                      cx="50%" cy="50%"
                      innerRadius={55} outerRadius={85}
                      paddingAngle={2}
                      label={(e) => `${e.name}: ${e.value}`}
                      labelLine={false}>
                      {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        )}
      </div>

      {/* v85.24 — Toggle master pesan otomatis */}
      <div className="border border-border rounded-sm bg-white p-4" data-testid="auto-msg-settings">
        <div className="flex items-center gap-2 mb-3">
          <Megaphone className="w-4 h-4 text-amber-700" />
          <h2 className="font-semibold">Pesan Otomatis Sistem</h2>
          <span className="text-[11px] text-zinc-500">
            Admin bisa jeda / jalankan ulang pengiriman otomatis.
          </span>
          {autoMsg.updated_at && (
            <span className="ml-auto text-[10px] text-zinc-400">
              Terakhir diubah {(autoMsg.updated_at || "").slice(0, 16).replace("T", " ")}
              {autoMsg.updated_by ? ` oleh ${autoMsg.updated_by}` : ""}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            {
              key: "followup_enabled",
              icon: <Send className="w-4 h-4 text-blue-700" />,
              title: "Follow-up & Tracking",
              desc: "Pesan tracking (ETA + link maps) & follow-up pasca-WO.",
              testid: "toggle-followup",
            },
            {
              key: "reminder_enabled",
              icon: <CalendarClock className="w-4 h-4 text-emerald-700" />,
              title: "Reminder Service",
              desc: "Reminder v1 (sesudah DONE) & v2 (menjelang jadwal berikutnya).",
              testid: "toggle-reminder",
            },
            {
              key: "force_qo_enabled",
              icon: <Bell className="w-4 h-4 text-rose-700" />,
              title: "CTA Quick Order",
              desc: "Auto-kirim template Quick Order saat customer chat masuk.",
              testid: "toggle-force-qo",
            },
          ].map(cfg => {
            const on = !!autoMsg[cfg.key];
            return (
              <div key={cfg.key}
                className={`border rounded-sm p-3 flex flex-col gap-2 ${
                  on ? "border-emerald-300 bg-emerald-50/40" : "border-zinc-300 bg-zinc-50"
                }`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {cfg.icon}
                    <div className="font-semibold text-[13px]">{cfg.title}</div>
                  </div>
                  <Switch
                    checked={on}
                    onCheckedChange={(v) => updateAutoMsg(cfg.key, !!v)}
                    disabled={autoMsgLoading}
                    data-testid={cfg.testid}
                  />
                </div>
                <div className="text-[11px] text-zinc-600 leading-relaxed">{cfg.desc}</div>
                <div className={`text-[10px] font-bold uppercase tracking-widest mt-auto ${
                  on ? "text-emerald-700" : "text-rose-700"
                }`}>
                  {on ? "● Aktif — Sistem mengirim otomatis" : "⏸ Dihentikan — Tidak kirim"}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <PushStatsCard />
    </div>
  );
}
